import {
  Alert,
  AlertSection,
  AlertType,
  AloftPrice,
  isDefined,
  isUndefined,
  schAloftArea,
} from '@aloft/toolkit-shared';

import { useMemo } from 'react';

import {
  getVacantLandChartData,
  getVacantLandSalesExponentialFit,
} from '../../../../components/graphs/costApproach/utils.ts';
import { useVacantLandSales } from '../../../../hooks/useVacantLandSales.tsx';
import { selectCompAdjustments } from '../../../../redux/features/selectors/adjustmentSelectors.ts';
import { selectAlerts } from '../../../../redux/features/selectors/commentarySelectors/selectAlerts.ts';
import { featuresSelectors } from '../../../../redux/features/selectors/featuresSelectors.ts';
import {
  selectCompSequence,
  selectCompTypes,
  selectOrder,
} from '../../../../redux/features/selectors/orderSelectors.ts';
import { selectPriorSales } from '../../../../redux/features/selectors/priorSalesSelectors.ts';
import { selectEditablePropertyDataById } from '../../../../redux/features/selectors/propertyDataSelectors/editablePropertyDataSelectors.ts';
import { selectImprovementEntityIds } from '../../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyDataEntities } from '../../../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import { selectImprovementsCosts } from '../../../../redux/features/selectors/selectImprovementsCost.ts';
import { selectReconciliation } from '../../../../redux/features/selectors/selectReconciliation.ts';
import { selectSiteCostPerSf } from '../../../../redux/features/selectors/selectSiteCostPerSf.ts';
import { selectSiteValues } from '../../../../redux/features/selectors/selectSiteValue.ts';
import { useAppSelector } from '../../../../redux/hooks.ts';
import { usePropertyImageEntities } from '../../../../utils/usePropertyImages.tsx';
import { allCompsAdjustedDown } from '../../../alerts/definitions/adjustments/allCompsAdjustedDown.ts';
import { allCompsAdjustedUp } from '../../../alerts/definitions/adjustments/allCompsAdjustedUp.ts';
import { bracketedMarketValue } from '../../../alerts/definitions/adjustments/bracketedMarketValue.ts';
import { conditionAdjustedUpAgainstSubject } from '../../../alerts/definitions/adjustments/conditionAdjustedUpAgainstSubject.ts';
import { limitedAdjustedSalePriceRange } from '../../../alerts/definitions/adjustments/limitedAdjustedSalePriceRange.ts';
import { bracketedGLA } from '../../../alerts/definitions/comparable/bracketedGLA.ts';
import { bracketedSiteSize } from '../../../alerts/definitions/comparable/bracketedSiteSize.ts';
import { lessThanThreeSettledSales } from '../../../alerts/definitions/comparable/lessThanThreeSettledSales.ts';
import { outsideProximityRequirement } from '../../../alerts/definitions/comparable/outsideProximityRequirement.ts';
import { propertyWithoutPhoto } from '../../../alerts/definitions/comparable/propertyWithoutPhoto.ts';
import { similarComparableToSubjectPriorSale } from '../../../alerts/definitions/comparable/similarComparableToSubjectPriorSale.ts';
import { bracketedSiteToValueRatio } from '../../../alerts/definitions/depreciatedCost/bracketedSiteToValueRatio.ts';
import { effectiveAgeLowerThanActualAge } from '../../../alerts/definitions/depreciatedCost/effectiveAgeLowerThanActualAge.ts';
import { inconsistentCompGlaAdjustment } from '../../../alerts/definitions/depreciatedCost/inconsistentCompGlaAdjustment.ts';
import { inconsistentCompSiteAdjustment } from '../../../alerts/definitions/depreciatedCost/inconsistentCompSiteAdjustment.ts';
import { largestSiteHasHighestValue } from '../../../alerts/definitions/depreciatedCost/largestSiteHasHighestValue.ts';
import { limitedSiteToValueRatioRange } from '../../../alerts/definitions/depreciatedCost/limitedSiteToValueRatioRange.ts';
import { negativeSiteValue } from '../../../alerts/definitions/depreciatedCost/negativeSiteValue.ts';
import { siteValueIsLessThanVacantLandValue } from '../../../alerts/definitions/depreciatedCost/siteValueIsLessThanVacantLandValue.ts';
import { smallestSiteHasLowestValue } from '../../../alerts/definitions/depreciatedCost/smallestSiteHasLowestValue.ts';
import { AlertDefinition, AlertDefinitionParams } from '../../../alerts/types.ts';

const buildAlertSection = (
  alertDefinitions: AlertDefinition[],
  params: AlertDefinitionParams,
  dismissedAlerts: AlertType[],
): Alert[] =>
  alertDefinitions
    .map((alertDefinition) => alertDefinition(params))
    .filter(isDefined)
    .map((alert) => ({
      ...alert,
      status: dismissedAlerts.includes(alert.type) ? 'dismissed' : 'active',
    }));

const splitCompSequenceByType = (
  compSequence: string[],
  compTypes: { [key: string]: 'arv' | 'aiv' | undefined } | undefined,
) => ({
  aiv: compSequence.filter((id) => !compTypes?.[id] || compTypes?.[id] === 'aiv'),
  arv: compSequence.filter((id) => compTypes?.[id] === 'arv'),
});

export const useAlerts = (): Record<AlertSection, Alert[]> => {
  const { dismissedAlerts } = useAppSelector(selectAlerts);
  const compAdjustments = useAppSelector(selectCompAdjustments);
  const compSequence = useAppSelector(selectCompSequence);
  const compTypes = useAppSelector(selectCompTypes);
  const improvementEntityIds = useAppSelector(selectImprovementEntityIds);
  const improvementCosts = useAppSelector(selectImprovementsCosts);
  const order = useAppSelector(selectOrder);
  const priorSales = useAppSelector(selectPriorSales);
  const propertyDataEntities = useAppSelector(selectPropertyDataEntities);
  const siteValues = useAppSelector(selectSiteValues);
  const reconciliation = useAppSelector(selectReconciliation);
  const propertyImageEntities = usePropertyImageEntities();

  const shouldShowNewCostApproachPage = useAppSelector(
    featuresSelectors.selectShouldShowNewCostApproachPage,
  );
  const vacantLandSales = useVacantLandSales();
  const subjectEditableData = useAppSelector((state) =>
    selectEditablePropertyDataById(state, 'subject'),
  );
  const subjectCostPerSF = useAppSelector((state) =>
    selectSiteCostPerSf(state, 'subject'),
  )?.toDollarLike();

  const vacantLandValue = useMemo(() => {
    const subjectSiteArea = subjectEditableData?.site?.area;
    const vacantLandSalesChartData = getVacantLandChartData(vacantLandSales);
    if (
      !vacantLandSales ||
      !subjectSiteArea ||
      isUndefined(subjectCostPerSF) ||
      !vacantLandSalesChartData ||
      vacantLandSalesChartData.length < 2
    ) {
      return undefined;
    }

    const siteSize = schAloftArea.parse(subjectSiteArea).getUnitAmount('sqft');

    const { getCostPerSFFromSiteSize } = getVacantLandSalesExponentialFit(
      vacantLandSalesChartData,
    );

    return AloftPrice.fromDollarLike(
      Math.min(getCostPerSFFromSiteSize(siteSize) * siteSize, Number.MAX_VALUE / 100),
    );
  }, [vacantLandSales, subjectEditableData?.site?.area, subjectCostPerSF]);

  if (
    [
      dismissedAlerts,
      compAdjustments,
      compSequence,
      improvementCosts,
      order,
      priorSales,
      propertyDataEntities,
      siteValues,
      propertyImageEntities,
    ].some(isUndefined)
  ) {
    return {
      adjustments: [],
      comparable: [],
      depreciatedCost: [],
    };
  }

  const alertDefinitionParams: AlertDefinitionParams = useMemo(
    () => ({
      compAdjustments,
      compSequence,
      improvementCosts,
      order,
      priorSales,
      propertyDataEntities,
      siteValues,
      improvementEntityIds,
      reconciliation,
      propertyImageEntities,
      vacantLandValue,
    }),
    [
      compAdjustments,
      compSequence,
      improvementCosts,
      order,
      priorSales,
      propertyDataEntities,
      siteValues,
      improvementEntityIds,
      reconciliation,
      propertyImageEntities,
      vacantLandValue,
    ],
  );

  const adjustments: Alert[] = useMemo(() => {
    const definitions = [
      limitedAdjustedSalePriceRange,
      allCompsAdjustedDown,
      allCompsAdjustedUp,
      bracketedMarketValue,
      conditionAdjustedUpAgainstSubject,
    ];

    if (!order.hasArvOption) {
      return buildAlertSection(definitions, alertDefinitionParams, dismissedAlerts);
    }

    const { aiv: aivCompSequence, arv: arvCompSequence } = splitCompSequenceByType(
      compSequence,
      compTypes,
    );

    const aivAlerts = buildAlertSection(
      definitions,
      { ...alertDefinitionParams, compSequence: aivCompSequence, compType: 'aiv' },
      dismissedAlerts,
    );
    const arvAlerts = buildAlertSection(
      definitions,
      { ...alertDefinitionParams, compSequence: arvCompSequence, compType: 'arv' },
      dismissedAlerts,
    );

    return [...aivAlerts, ...arvAlerts];
  }, [alertDefinitionParams, dismissedAlerts, order.hasArvOption, compSequence, compTypes]);

  const comparable: Alert[] = useMemo(() => {
    const definitions = [
      lessThanThreeSettledSales,
      bracketedGLA,
      bracketedSiteSize,
      similarComparableToSubjectPriorSale,
      propertyWithoutPhoto,
    ];

    if (!order.hasArvOption) {
      return buildAlertSection(definitions, alertDefinitionParams, dismissedAlerts);
    }

    const { aiv: aivCompSequence, arv: arvCompSequence } = splitCompSequenceByType(
      compSequence,
      compTypes,
    );

    const aivAlerts = buildAlertSection(
      definitions,
      { ...alertDefinitionParams, compSequence: aivCompSequence, compType: 'aiv' },
      dismissedAlerts,
    );
    const arvAlerts = buildAlertSection(
      definitions,
      { ...alertDefinitionParams, compSequence: arvCompSequence, compType: 'arv' },
      dismissedAlerts,
    );

    const proximityAlert = buildAlertSection(
      [outsideProximityRequirement],
      { ...alertDefinitionParams, compSequence },
      dismissedAlerts,
    );

    return [...aivAlerts, ...arvAlerts, ...proximityAlert];
  }, [
    alertDefinitionParams,
    dismissedAlerts,
    order.hasArvOption,
    compSequence,
    compTypes,
    propertyImageEntities,
  ]);

  const depreciatedCost: Alert[] = useMemo(
    () =>
      buildAlertSection(
        [
          effectiveAgeLowerThanActualAge,
          negativeSiteValue,
          ...(shouldShowNewCostApproachPage ? [siteValueIsLessThanVacantLandValue] : []),
          smallestSiteHasLowestValue,
          largestSiteHasHighestValue,
          bracketedSiteToValueRatio,
          limitedSiteToValueRatioRange,
          inconsistentCompSiteAdjustment,
          inconsistentCompGlaAdjustment,
        ],
        alertDefinitionParams,
        dismissedAlerts,
      ),
    [alertDefinitionParams, dismissedAlerts, shouldShowNewCostApproachPage],
  );

  return {
    comparable,
    depreciatedCost,
    adjustments,
  };
};
